import { Component, OnInit, Output, EventEmitter, Input, ViewChild, SimpleChanges } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { debounceTime, filter, distinctUntilChanged, map, finalize, takeUntil } from 'rxjs/operators';
import { CampaignsService } from 'src/app/pages/campaign-module/campaigns/campaigns.service';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { of as observableOf, Subscription, fromEvent } from 'rxjs';
import { DialogService, LanguageService } from 'src/app/services';
import { environment } from 'src/environments/environment';
import { requestChainType } from 'src/app/model/requests';
import * as _ from 'lodash';

@Component({
  selector: 'esc-chain-type-dropdown',
  templateUrl: './chain-type-dropdown.component.html',
  styleUrls: ['./chain-type-dropdown.component.scss']
})
export class ChainTypeDropdownComponent implements OnInit {
  @Input() wrapperLayoutAlign;
  @Input() formWrapperFlex;
  @Input() previousData;
  @Input() requiredError;
  @Input() isRequired;
  @Input() disabled;
  @Output() optionSelected = new EventEmitter;
  @Output() errorLoading = new EventEmitter;
  
  @ViewChild('chain_typeAC') chain_typeACRef: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) chain_typeACTr: MatAutocompleteTrigger;

  form: FormGroup;
  loading = {
    chain_type: false,
    init: false,
    more: false,
    error: false
  };
  public listData = [];
  private currentFilters;
  private listSub: Subscription;
  networkErrMsg = this.languageService.getTranslation("MSG_ERROR_NETWORK_CONNECTION", {});

  constructor(
    private fb: FormBuilder,
    public campSvc: CampaignsService,
    private dialogService: DialogService,
    private languageService: LanguageService,
    ) { }

  ngOnInit() {
    this.currentFilters = _.cloneDeep(requestChainType);
    this.initForm();
    this.detectFormChanges();
    // console.log(this.previousData);
  }

  ngOnDestroy() {
    if (this.listSub && !this.listSub.closed) {
      this.listSub.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if(this.form && _.get(changes, 'requiredError') && changes.requiredError.currentValue) {
      this.form.get('chain_type').markAsTouched();
    }
    
    if (this.form && _.get(changes, 'disabled') && changes.disabled.currentValue) {
      this.form.disable()
    }
    else if (this.form && _.get(changes, 'disabled') && !changes.disabled.currentValue) {
      this.form.enable()
    }
  }

  initForm() {
    this.form = this.fb.group({
      chain_type: [null, Validators.required]
    })

    if(_.get(this.previousData, 'chain_type')) {
      let chain_typeObj = {
        id: _.get(this.previousData, 'chain_type.id') || null,
        code: _.get(this.previousData, 'chain_type.code') || null,
        description: _.get(this.previousData, 'chain_type.description') || null,
      }
      this.form.get('chain_type').setValue(chain_typeObj, { emitEvent: true });
      this.currentFilters.web_filter.keyword = _.get(this.previousData, 'chain_type.description');
      this.getListData('edit');
    }
  }

  detectFormChanges() {
    this.form.get('chain_type').valueChanges.pipe(
      debounceTime(600),
      filter((term) => !_.isObject(term)),
      distinctUntilChanged(),
      map((term: string) => _.trim(term)),
    )
    .subscribe(value => {
      this.currentFilters.web_filter.keyword = _.toString(value);
      this.listData = [];
      this.getListData(null);
    });
  }

  getInitialValues(data) {
    this.currentFilters.web_filter.offset = 0;
    this.currentFilters.web_filter.keyword = "";
    this.listData = [];
    this.getListData(null);
  }
   
  getMoreData(name) {
    if (this.currentFilters.getMore) {
      this.currentFilters.web_filter.offset += requestChainType.web_filter.limit;
      this.getListData(name);
    }
  }

  getListData(mode) {
    this.loading.chain_type = true;
    if (this.loading.init == true) {
      return;
    }
    this.loading.error = false;
    if (this.listData.length == 0) {
      this.loading.init = true;
    } else {
      this.loading.more = true;
    }

    this.listSub = this.campSvc.getChaintypes(this.currentFilters).pipe(
      finalize(() => {
        this.listSub.unsubscribe();
        return observableOf(null);
      }))
      .subscribe((res: any) => {
        if (environment.showLogs) {
          console.log(res);
        }
        this.loading.chain_type = false;
        this.loading.init = false;
        this.loading.more = false;
        if (res == null || res.status == 0 || res.status == 400 || res.status == 500 || res.status == 502 || res.status == 504) {
          this.loading.error = true;
          this.listData = [];
          this.dialogService.openSnackBar(this.networkErrMsg)
          if(mode == 'edit') { 
            this.errorLoading.next(true);
          }
          return;
        }
        
        if (_.has(res, 'value') && (res.value == "Success")) {
          this.listData = this.listData.concat(res.data);

          if((res.data.length > 0) && (this.listData.length < _.get(res, 'count'))) {
            this.currentFilters.getMore = true;
          }
        }

        if((_.toLower(_.get(res, 'value')) == "empty object") && (this.currentFilters.getMore == true)) {
          this.currentFilters.getMore = false;
        }

      }, err => {
        if (environment.showLogs) {
          console.error(err);
        }
        if(mode == 'edit') { 
          this.errorLoading.next(true);
        }
        this.loading.chain_type = false;
        if (this.listData.length == 0) {
          this.loading.error = true;
        } else {
          this.dialogService.openSnackBar(this.languageService.getTranslation("MSG_INTERNAL_SERVER_ERROR", {}));
          this.currentFilters.web_filter.limit -= this.currentFilters.web_filter.offset;
        }
        return observableOf(null);
      });
  }

  filterListdata() {
    let chain_typeObj = {};
    chain_typeObj = this.listData.filter(e => {
      return e.id == _.get(this.previousData, 'chain_type.id')
    })
    this.form.get('chain_type').setValue(chain_typeObj[0]);
    this.autoCmpltClosed();
  }

  displayFn(data?: any): string | undefined {
    if(data) {
      return _.get(data, 'code') != "all" ? (_.get(data, 'code') + ' - ' + _.get(data, 'description')) : _.get(data, 'description');
    }
    return null;
  }

  autoCmpltClosed() {
    this.listData = [];

    if (_.isObject(_.get(this.form.get('chain_type'), 'value'))) {
      this.optionSelected.next(_.get(this.form.get('chain_type'), 'value'))
    }
  }

  clearForm(ev: Event) {
    this.form.get('chain_type').setValue(null, { emitEvent: false });
    this.optionSelected.next(null)
    // ev.stopPropagation();
  }

  autocompleteScroll(name, refName, acTrigger) {
    setTimeout(() => {
      if (this[refName].isOpen || this[refName].panel) {
        fromEvent(this[refName].panel.nativeElement, 'scroll')
          .pipe(
            map(x => this[refName].panel.nativeElement.scrollTop),
            takeUntil(this[acTrigger].panelClosingActions)
          )
          .subscribe(x => {
            const scrollTop = this[refName].panel.nativeElement
              .scrollTop;
            const scrollHeight = this[refName].panel.nativeElement
              .scrollHeight;
            const elementHeight = this[refName].panel.nativeElement
              .clientHeight;
            const atBottom = scrollHeight === scrollTop + elementHeight;
            if (atBottom) {
              // fetch more data
              this.getMoreData(name);
            }
          });
      }
    });
  }

  checkAutocompleteObject(control, objectKey) {
    if ((!_.isObject(control.controls[objectKey].value) || control.controls[objectKey].value.id == null) && (control.controls[objectKey].value && control.controls[objectKey].value != "")) {
      control.controls[objectKey].setErrors({
        "notExisting": true
      });
      
      this.optionSelected.next(null)
    }
  }
}